import { useCallback, useEffect, useState } from "react";
import {
  applyLibraryCommand,
  emptyLibrary,
  studentLibrary,
  type LibraryCommand,
  type LibraryState,
  type StudentRef,
} from "./activity-library";
import { initialCatalog, type CatalogCompetency } from "./catalog";
import type { SchoolClass } from "./ClassesView";
import {mergeLiveUpdate, type LiveUpdate} from "./activity-live";
import {dopplerCorrectionsNeedUpgrade} from "./doppler-correction-upgrade";
import {useActivityLive} from "./useActivityLive";

export type LibraryResponse = {
  state: LibraryState;
  catalog: CatalogCompetency[];
  classes: SchoolClass[];
  role: "teacher" | "student";
  migrationWarnings?: string[];
};
export const libraryDemo =
  import.meta.env.DEV &&
  new URLSearchParams(location.search).get("libraryDemo") === "1";
const demoKey = "library-development-demo-v1";
const demoClasses: SchoolClass[] = [
  {
    id: "demo-library-class",
    name: "Terminale — démonstration locale",
    schoolLevel: "Tle Spé",
    subject: "Physique-chimie",
    students: [
      {
        id: "demo-library-1",
        firstName: "Camille",
        lastName: "Martin",
        sex: "femme",
        officialGroup: "A",
      },
      {
        id: "demo-library-2",
        firstName: "Alex",
        lastName: "Durand",
        sex: "homme",
        officialGroup: "B",
      },
    ],
  },
];

let referenceSyncPromise: Promise<LibraryResponse> | null = null;
async function synchronizeReferences(response: LibraryResponse, student?: StudentRef): Promise<LibraryResponse> {
  if (student || response.role !== "teacher") return response;
  if (!referenceSyncPromise) {
    referenceSyncPromise = (async () => {
      const {synchronizeReferenceActivities, publishDopplerCorrections,publishVinegarPresentationCorrections} = await import("./reference-library-installer");
      let latest = await synchronizeReferenceActivities(libraryRequest);
      latest=await publishVinegarPresentationCorrections(libraryRequest);
      if (dopplerCorrectionsNeedUpgrade(latest.state)) {
        await publishDopplerCorrections(libraryRequest);
        latest = await libraryRequest();
      }
      return latest;
    })().finally(() => { referenceSyncPromise = null; });
  }
  return referenceSyncPromise;
}

export async function libraryRequest(
  command?: LibraryCommand,
  student?: StudentRef,
  cached?: LibraryResponse,
): Promise<LibraryResponse> {
  if (libraryDemo) {
    let state: LibraryState =
      JSON.parse(localStorage.getItem(demoKey) ?? sessionStorage.getItem(demoKey) ?? "null") ?? emptyLibrary();
    const { initializeReferenceLibrary } = await import("./library-reference-activities");
    const initialized = initializeReferenceLibrary(state, initialCatalog);
    if (initialized.state !== state) {
      state = initialized.state;
      localStorage.setItem(demoKey, JSON.stringify(state));
    }
    // Adopt an existing local session once; persist the same IDs across restarts.
    if (!localStorage.getItem(demoKey)) localStorage.setItem(demoKey, JSON.stringify(state));
    if (command) {
      state = applyLibraryCommand(
        state,
        command,
        student ? { role: "student", ...student } : { role: "teacher", teacherId: "local-demo" },
        initialCatalog,
      );
      localStorage.setItem(demoKey, JSON.stringify(state));
    }
    return {
      state: student ? studentLibrary(state, student) : state,
      catalog: initialCatalog,
      classes: demoClasses,
      role: student ? "student" : "teacher",
      migrationWarnings: initialized.warnings,
    };
  }
  let query = student
    ? `?classId=${encodeURIComponent(student.classId)}&studentId=${encodeURIComponent(student.studentId)}`
    : "";
  const compact = cached && command?.type === "answer";
  if (compact) query += `${query ? "&" : "?"}response=attempt`;
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  let response: Response;
  try {
    response = await fetch(`/api/activity-library${query}`, {
      credentials: "include",
      signal: controller.signal,
      ...(command
        ? {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(command),
          }
        : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new Error("Le chargement de la bibliothèque prend trop de temps. Réessayez.");
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.headers.get("content-type")?.includes("application/json"))
    throw new Error(
      "La bibliothèque nécessite le serveur CompetenceO et une connexion à votre compte.",
    );
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "Bibliothèque indisponible.");
  if (compact) return {...cached, state: mergeLiveUpdate(cached.state, {attempts: [body.attempt], session: null, presence: [], removedAttemptIds: []})};
  return body;
}

export function useActivityLibrary(student?: StudentRef, liveAttemptId?: string | null) {
  const [data, setData] = useState<LibraryResponse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const classId = student?.classId,
    studentId = student?.studentId;
  const refresh = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const studentRef =
        classId && studentId ? { classId, studentId } : undefined;
      const response = await libraryRequest(undefined, studentRef);
      setData(response);
      setData(await synchronizeReferences(response, studentRef));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Chargement impossible.");
    } finally {
      setBusy(false);
    }
  }, [classId, studentId]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const live = useActivityLive(data?.state, {attemptId: liveAttemptId ?? undefined}, update => {
    if (update.attempts.some(a => a.restitution?.status === "released" && !data?.state.attempts.find(old => old.id === a.id)?.restitution)) {
      void refresh(); // Fetch the newly released correction snapshot once.
    } else setData(previous => {
      if (!previous) return previous;
      const state = mergeLiveUpdate(previous.state, update);
      return state === previous.state ? previous : {...previous, state};
    });
  }, !libraryDemo && data?.role === "student");
  async function execute(command: LibraryCommand) {
    if (command.type !== "answer") setBusy(true);
    setError("");
    try {
      const result = await libraryRequest(command, student, data ?? undefined);
      if (command.type === "answer") setData(previous => previous ? {...previous, state: mergeLiveUpdate(previous.state, {attempts: result.state.attempts.filter(a => a.id === command.attemptId), session: null, presence: [], removedAttemptIds: []})} : result);
      else setData(result);
      return result;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Enregistrement impossible.");
      throw e;
    } finally {
      if (command.type !== "answer") setBusy(false);
    }
  }
  const applyLive = (update: LiveUpdate) => setData(previous => {
    if (!previous) return previous;
    const state = mergeLiveUpdate(previous.state, update);
    return state === previous.state ? previous : {...previous, state};
  });
  return { data, error: error || live.error, busy, execute, refresh, applyLive };
}

